import {
  type FormEvent,
  type JSX,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import { routes } from '../routes';
import { AppLink, useOptionalNavigator } from '../discovery/router';
import { usePageMeta } from '../discovery/page-meta';
import {
  SessionApiError,
  completeMfa,
  requestSignInCode,
  signInWithCode,
  signInWithPassword,
} from '../session/client';
import { useSession } from '../session/SessionProvider';
import { SoonLink } from './SiteFooter';
import { peekReturnPath, takeReturnPath, useTheme } from './preferences';

/**
 * Log in or create account — `Just Treated Log In.dc.html`, built as drawn.
 *
 * The frame's three steps are here: the email, the six-digit code (with resend
 * on a 30-second cooldown and "Use a different email"), and "You're signed in".
 * They are real: the code is issued and checked by identity, and signing in
 * opens a session held in an httpOnly cookie (see `session/client.ts`).
 *
 * ## Where this differs from the frame, and why (owner decisions, 2026-11-01)
 *
 * - **No Google, Facebook or Apple buttons, and so no "or" divider** (D4: social
 *   sign-in waits until those apps exist).
 * - **No Cloudflare box** (D4: the server rate-limits instead of Turnstile).
 * - **"Use a password instead"** is added (D4): the existing password sign-in,
 *   needed while email cannot be delivered on beta and for accounts that use
 *   an authenticator app, which then asks for its code.
 * - The done step's "Your vouchers and orders are now in your account" is cut
 *   to "Welcome to Just Treated." — the platform has no vouchers (D8) and does
 *   not yet move orders into an account.
 * - The logo and "Continue to Just Treated" go to this site's homepage, which is
 *   what the frame's Places Near You page stood in for.
 */

type Step = 'email' | 'code' | 'password' | 'mfa' | 'done';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const RESEND_SECONDS = 30;

function minutes(seconds: unknown): string {
  const value = typeof seconds === 'number' && seconds > 0 ? Math.ceil(seconds / 60) : 1;
  return value === 1 ? 'a minute' : `${value} minutes`;
}

/** Plain words for every answer the server can give. Never more precise than the server is. */
export function messageFor(error: unknown, context: 'request' | 'code' | 'password' | 'mfa'): string {
  if (!(error instanceof SessionApiError)) return 'Something went wrong. Try again.';
  switch (error.code) {
    case 'AUTH_RATE_LIMITED':
      return `Too many attempts. Try again in ${minutes(error.details.retryAfterSeconds)}.`;
    case 'AUTH_CHALLENGE_INVALID':
      return "That code isn't right, or it has expired. Check your email, or resend a new code.";
    case 'AUTH_INVALID_CREDENTIALS':
      return "That email and password don't match. Check them, or use an email code instead.";
    case 'AUTH_VERIFICATION_REQUIRED':
      return 'This email address has not been verified yet. Use an email code instead — it verifies it.';
    case 'AUTH_MFA_INVALID':
      return "That code isn't right. Check your authenticator app and try again.";
    case 'VALIDATION_FAILED':
      return context === 'request'
        ? 'Enter a valid email address, like name@example.com'
        : context === 'code' || context === 'mfa'
          ? 'Enter the 6-digit code'
          : 'Check your email address and password.';
    default:
      return error.message;
  }
}

export function LoginPage(): JSX.Element {
  usePageMeta({ title: 'Log in or create account', noindex: true });
  useTheme();
  const session = useSession();
  const navigator = useOptionalNavigator();
  const ids = useId();

  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [mfaToken, setMfaToken] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [emailErr, setEmailErr] = useState('');
  const [codeErr, setCodeErr] = useState('');
  const [passwordErr, setPasswordErr] = useState('');
  /** Shown above the password form when the code path sent the person there. */
  const [notice, setNotice] = useState('');
  const [cool, setCool] = useState(0);
  const [toast, setToast] = useState('');

  // Already signed in when the page opened: say so rather than ask again.
  const alreadySignedIn = session.status === 'signedIn' && step === 'email' && !busy;
  const shownStep: Step = alreadySignedIn ? 'done' : step;

  useEffect(() => {
    if (cool <= 0) return undefined;
    const timer = window.setTimeout(() => setCool((value) => Math.max(0, value - 1)), 1000);
    return () => window.clearTimeout(timer);
  }, [cool]);

  useEffect(() => {
    if (toast === '') return undefined;
    const timer = window.setTimeout(() => setToast(''), 2400);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const finish = useCallback(() => {
    setBusy(false);
    setStep('done');
    // The frame sends the visitor back to the page they came from, after a beat.
    const next = takeReturnPath();
    if (next !== undefined) window.setTimeout(() => navigator?.navigate(next), 900);
  }, [navigator]);

  const sendCode = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    const trimmed = email.trim();
    if (trimmed === '') return setEmailErr('Enter your email address');
    if (!EMAIL_PATTERN.test(trimmed))
      return setEmailErr('Enter a valid email address, like name@example.com');

    setBusy(true);
    try {
      await requestSignInCode(trimmed);
      setCode('');
      setCodeErr('');
      setStep('code');
      setCool(RESEND_SECONDS);
    } catch (error) {
      setEmailErr(messageFor(error, 'request'));
    } finally {
      setBusy(false);
    }
  };

  const verify = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    if (code.length !== 6) return setCodeErr('Enter the 6-digit code from your email');

    setBusy(true);
    try {
      await signInWithCode(email.trim(), code);
      finish();
    } catch (error) {
      setBusy(false);
      if (error instanceof SessionApiError && error.code === 'AUTH_PASSWORD_SIGN_IN_REQUIRED') {
        setNotice(error.message);
        setStep('password');
        return;
      }
      setCodeErr(messageFor(error, 'code'));
    }
  };

  const resend = async (): Promise<void> => {
    if (cool > 0) return;
    setCool(RESEND_SECONDS);
    try {
      await requestSignInCode(email.trim());
      setToast(`New code sent to ${email.trim()}`);
    } catch (error) {
      setToast(messageFor(error, 'request'));
    }
  };

  const passwordSignIn = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    const trimmed = email.trim();
    if (trimmed === '' || !EMAIL_PATTERN.test(trimmed)) {
      return setPasswordErr('Enter a valid email address, like name@example.com');
    }
    if (password === '') return setPasswordErr('Enter your password');

    setBusy(true);
    try {
      await signInWithPassword(trimmed, password);
      setPassword('');
      finish();
    } catch (error) {
      setBusy(false);
      if (error instanceof SessionApiError && error.code === 'AUTH_MFA_REQUIRED') {
        const token = error.details.mfaToken;
        setMfaToken(typeof token === 'string' ? token : undefined);
        setPassword('');
        setCode('');
        setCodeErr('');
        setStep('mfa');
        return;
      }
      setPasswordErr(messageFor(error, 'password'));
    }
  };

  const verifyMfa = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    if (code.length !== 6) return setCodeErr('Enter the 6-digit code from your authenticator app');
    if (mfaToken === undefined) {
      setStep('password');
      return setPasswordErr('Log in with your password again.');
    }
    setBusy(true);
    try {
      await completeMfa(mfaToken, code);
      finish();
    } catch (error) {
      setBusy(false);
      setCodeErr(messageFor(error, 'mfa'));
    }
  };

  const toPassword = (): void => {
    setNotice('');
    setPasswordErr('');
    setStep('password');
  };

  const toEmail = (): void => {
    setEmailErr('');
    setCode('');
    setCodeErr('');
    setStep('email');
  };

  const goBack = (): void => {
    const next = peekReturnPath();
    if (next !== undefined) return navigator?.navigate(next);
    let sameSite = false;
    try {
      sameSite = document.referrer !== '' && new URL(document.referrer).origin === window.location.origin;
    } catch {
      sameSite = false;
    }
    if (sameSite && window.history.length > 1) return window.history.back();
    navigator?.navigate(routes.home.path);
  };

  // -- The frame's fit: the card scales down (to 80% at most) rather than scroll.
  const mainRef = useRef<HTMLElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  useLayoutEffect(() => {
    const fit = (): void => {
      const main = mainRef.current;
      const card = cardRef.current;
      if (main === null || card === null || card.offsetHeight === 0) return;
      const next = Math.max(0.8, Math.min(1, (main.clientHeight - 24) / card.offsetHeight));
      setScale((current) => (Math.abs(current - next) > 0.005 ? next : current));
    };
    fit();
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  });

  const codeStep = shownStep === 'code' || shownStep === 'mfa';

  return (
    <div className="jt-login">
      <header className="jt-login__header">
        <button type="button" className="jt-login__back" aria-label="Go back" onClick={goBack}>
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#5b2ede"
            strokeWidth="2.6"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M14.6 5.4 8 12l6.6 6.6" />
          </svg>
        </button>
        <AppLink to={routes.home.path} className="jt-login__logo" ariaLabel="Just Treated home">
          <span role="img" aria-label="Just Treated" className="jt-login__logo-img" />
        </AppLink>
      </header>

      <main ref={mainRef} className="jt-login__main" id="main">
        <div ref={cardRef} className="jt-login__card" style={{ transform: `scale(${scale})` }}>
          {shownStep === 'email' ? (
            <>
              <h1 className="jt-login__h1">Log in or create account</h1>
              <form onSubmit={(event) => void sendCode(event)} noValidate className="jt-login__form">
                <label className="jt-login__label">
                  <span className="jt-login__label-text" id={`${ids}-email`}>
                    Continue with email
                  </span>
                  <input
                    aria-labelledby={`${ids}-email`}
                    value={email}
                    onChange={(event) => {
                      setEmail(event.target.value);
                      setEmailErr('');
                    }}
                    type="email"
                    autoComplete="email"
                    inputMode="email"
                    placeholder="name@example.com"
                    aria-invalid={emailErr !== ''}
                    className={`jt-login__input ${emailErr !== '' ? 'is-bad' : ''}`}
                  />
                  {emailErr !== '' ? (
                    <span className="jt-login__err" role="alert">
                      {emailErr}
                    </span>
                  ) : null}
                </label>
                <button type="submit" className="jt-login__primary" disabled={busy} aria-busy={busy}>
                  {busy ? <span className="jt-login__spin" aria-hidden="true" /> : null}Get verification code
                </button>
              </form>
              <button type="button" className="jt-login__alt" onClick={toPassword}>
                Use a password instead
              </button>
              <p className="jt-login__terms">
                By continuing you agree to our{' '}
                <SoonLink label="Terms and Conditions" className="jt-login__terms-link" />. Please read our{' '}
                <SoonLink label="Privacy Notice" className="jt-login__terms-link" /> and{' '}
                <SoonLink label="Cookie Policy" className="jt-login__terms-link" />.
              </p>
            </>
          ) : null}

          {codeStep ? (
            <>
              {shownStep === 'code' ? (
                <button type="button" className="jt-login__switch" onClick={toEmail}>
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#5b2ede"
                    strokeWidth="2.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M19 12H5M11 6l-6 6 6 6" />
                  </svg>
                  Use a different email
                </button>
              ) : (
                <button type="button" className="jt-login__switch" onClick={toPassword}>
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#5b2ede"
                    strokeWidth="2.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M19 12H5M11 6l-6 6 6 6" />
                  </svg>
                  Back to password
                </button>
              )}
              <h1 className="jt-login__h1">
                {shownStep === 'code' ? 'Check your inbox' : 'Enter your authenticator code'}
              </h1>
              {shownStep === 'code' ? (
                <p className="jt-login__p">
                  We've sent a 6-digit code to <b>{email.trim()}</b>. It expires in 10 minutes.
                </p>
              ) : (
                <p className="jt-login__p">
                  Open your authenticator app and enter the 6-digit code it shows for Just Treated.
                </p>
              )}
              <form
                onSubmit={(event) => void (shownStep === 'code' ? verify(event) : verifyMfa(event))}
                noValidate
                className="jt-login__form"
              >
                <label className="jt-login__label jt-login__label--code">
                  <span className="jt-login__label-text" id={`${ids}-code`}>
                    Verification code
                  </span>
                  <input
                    aria-labelledby={`${ids}-code`}
                    value={code}
                    onChange={(event) => {
                      setCode(event.target.value.replace(/\D/g, '').slice(0, 6));
                      setCodeErr('');
                    }}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    placeholder="000000"
                    aria-invalid={codeErr !== ''}
                    className={`jt-login__code ${codeErr !== '' ? 'is-bad' : ''}`}
                  />
                  {codeErr !== '' ? (
                    <span className="jt-login__err" role="alert">
                      {codeErr}
                    </span>
                  ) : null}
                </label>
                <button type="submit" className="jt-login__primary" disabled={busy} aria-busy={busy}>
                  {busy ? <span className="jt-login__spin" aria-hidden="true" /> : null}Continue
                </button>
              </form>
              {shownStep === 'code' ? (
                <div className="jt-login__resend">
                  Didn't get it?
                  <button
                    type="button"
                    onClick={() => void resend()}
                    disabled={cool > 0}
                    className={cool > 0 ? 'is-off' : ''}
                  >
                    {cool > 0 ? `Resend in ${cool}s` : 'Resend code'}
                  </button>
                </div>
              ) : null}
            </>
          ) : null}

          {shownStep === 'password' ? (
            <>
              <button type="button" className="jt-login__switch" onClick={toEmail}>
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#5b2ede"
                  strokeWidth="2.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M19 12H5M11 6l-6 6 6 6" />
                </svg>
                Use an email code instead
              </button>
              <h1 className="jt-login__h1">Log in with your password</h1>
              {notice !== '' ? (
                <p className="jt-login__p" role="status">
                  {notice}
                </p>
              ) : null}
              <form onSubmit={(event) => void passwordSignIn(event)} noValidate className="jt-login__form">
                <label className="jt-login__label">
                  <span className="jt-login__label-text" id={`${ids}-pwemail`}>
                    Email
                  </span>
                  <input
                    aria-labelledby={`${ids}-pwemail`}
                    value={email}
                    onChange={(event) => {
                      setEmail(event.target.value);
                      setPasswordErr('');
                    }}
                    type="email"
                    autoComplete="email"
                    inputMode="email"
                    placeholder="name@example.com"
                    className="jt-login__input"
                  />
                </label>
                <label className="jt-login__label">
                  <span className="jt-login__label-text" id={`${ids}-password`}>
                    Password
                  </span>
                  <input
                    aria-labelledby={`${ids}-password`}
                    value={password}
                    onChange={(event) => {
                      setPassword(event.target.value);
                      setPasswordErr('');
                    }}
                    type="password"
                    autoComplete="current-password"
                    aria-invalid={passwordErr !== ''}
                    className={`jt-login__input ${passwordErr !== '' ? 'is-bad' : ''}`}
                  />
                  {passwordErr !== '' ? (
                    <span className="jt-login__err" role="alert">
                      {passwordErr}
                    </span>
                  ) : null}
                </label>
                <button type="submit" className="jt-login__primary" disabled={busy} aria-busy={busy}>
                  {busy ? <span className="jt-login__spin" aria-hidden="true" /> : null}Log in
                </button>
              </form>
            </>
          ) : null}

          {shownStep === 'done' ? (
            <div className="jt-login__done">
              <span className="jt-login__tick" aria-hidden="true">
                <svg
                  width="26"
                  height="26"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#156b49"
                  strokeWidth="2.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="m5 12.5 4.2 4.2L19 7" />
                </svg>
              </span>
              <h1 className="jt-login__h1">You're signed in</h1>
              <p className="jt-login__p">Welcome to Just Treated.</p>
              <AppLink to={peekReturnPath() ?? routes.home.path} className="jt-login__continue">
                Continue to Just Treated
              </AppLink>
            </div>
          ) : null}
        </div>
      </main>

      <footer className="jt-login__footer">
        <span className="jt-login__copy">© {new Date().getFullYear()} Just Treated</span>
        <SoonLink label="Check my cookie preferences" className="jt-login__cookies" />
      </footer>

      {toast !== '' ? (
        <div role="status" className="jt-login__toast">
          {toast}
        </div>
      ) : null}
    </div>
  );
}
